import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/endpoints';
import { maskPlayerName } from '../utils/displayName';
import { hideRider, unhideRider, useHiddenRiders } from '../utils/hiddenRiders';

/**
 * Hide or report another rider (App Review guideline 1.2: user-generated
 * names need a way to report and to block). Rows in Live bets and Top riders
 * show a small ⋯ button; one sheet, mounted once on the game screen, serves
 * them all, so it survives the row leaving the list when the round ends.
 */

export interface RiderTarget { playerId: string; username?: string | null; }

let openTarget: RiderTarget | null = null;
const sheetListeners = new Set<() => void>();
const notifySheet = () => sheetListeners.forEach(listener => listener());
const subscribeSheet = (listener: () => void) => { sheetListeners.add(listener); return () => { sheetListeners.delete(listener); }; };
const getSheet = () => openTarget;

export function openRiderSheet(target: RiderTarget) { openTarget = target; notifySheet(); }
export function closeRiderSheet() { if (openTarget) { openTarget = null; notifySheet(); } }

export const RiderActionsButton: React.FC<{ playerId: string; username?: string | null }> = ({ playerId, username }) => {
  useHiddenRiders(); // the label names the rider as currently shown
  return <button
    type="button"
    className="rider-actions-button"
    aria-haspopup="dialog"
    aria-label={`Rider options for ${maskPlayerName(username, playerId)}`}
    title="Hide or report"
    onClick={() => openRiderSheet({ playerId, username })}
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
  </button>;
};

export type ReportReason = 'offensive_name' | 'cheating' | 'other';
const REASONS: { value: ReportReason; label: string }[] = [
  { value: 'offensive_name', label: 'Offensive name' },
  { value: 'cheating', label: 'Cheating' },
  { value: 'other', label: 'Something else' },
];
export const REPORT_NOTE_MAX = 200;

type Notice = { tone: 'ok' | 'error'; text: string } | null;

const FOCUSABLE = 'button:not(:disabled), [href], input:not(:disabled), textarea:not(:disabled)';
/** Keeps Tab inside the open sheet, as a modal dialog should. */
function trapTab(event: React.KeyboardEvent<HTMLElement>) {
  if (event.key !== 'Tab') return;
  const focusable = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE));
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
}

function reportResultMessage(status: number): Notice {
  if (status >= 200 && status < 300) return { tone: 'ok', text: "Thanks, we'll review it." };
  if (status === 429) return { tone: 'error', text: "You've sent a lot of reports in the last hour. Please try again a little later." };
  if (status === 401) return { tone: 'error', text: 'Your session has ended. Sign in again to report a rider.' };
  if (status === 400) return { tone: 'error', text: "That report couldn't be sent. Check the reason and note, then try again." };
  return { tone: 'error', text: "Reports aren't available right now. Please try again later." };
}

function RiderSheet({ target, onClose }: { target: RiderTarget; onClose: () => void }) {
  const { token } = useAuth();
  const hidden = useHiddenRiders().includes(target.playerId);
  const name = maskPlayerName(target.username, target.playerId);
  const [view, setView] = useState<'menu' | 'report'>('menu');
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [reported, setReported] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [onClose]);
  // Move focus into the sheet whenever its content changes view.
  useEffect(() => { sheetRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus(); }, [view, reported]);

  const toggleHidden = () => {
    if (hidden) {
      unhideRider(target.playerId);
      setNotice({ tone: 'ok', text: 'This rider shows normally again.' });
    } else {
      hideRider(target.playerId);
      setNotice({ tone: 'ok', text: 'Hidden on this device. Undo it any time in Display settings.' });
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!reason || sending) return;
    setSending(true);
    setNotice(null);
    const trimmed = note.trim().slice(0, REPORT_NOTE_MAX);
    try {
      const response = await fetch(`${API_URL}/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ riderId: target.playerId, reason, ...(trimmed ? { note: trimmed } : {}) }),
      });
      const result = reportResultMessage(response.status);
      setNotice(result);
      if (result?.tone === 'ok') setReported(true);
    } catch {
      setNotice({ tone: 'error', text: "Couldn't send the report. Check your connection and try again." });
    } finally {
      setSending(false);
    }
  };

  const status = notice && <p className={notice.tone === 'ok' ? 'auth-notice' : 'auth-error'} role={notice.tone === 'ok' ? 'status' : 'alert'}>{notice.text}</p>;

  return (
    <div className="rider-sheet-backdrop" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
      <div ref={sheetRef} className="rider-sheet" role="dialog" aria-modal="true" aria-labelledby="rider-sheet-title" onKeyDown={trapTab}>
        <div className="rider-sheet-heading">
          <div><p className="section-label">{view === 'report' ? 'Report rider' : 'Rider'}</p><h2 id="rider-sheet-title">{name}</h2></div>
          <button type="button" className="btn-quiet" onClick={onClose}>Close</button>
        </div>

        {view === 'menu' ? <div className="rider-sheet-actions">
          <button type="button" className="btn-quiet" data-autofocus onClick={toggleHidden}>{hidden ? 'Unhide rider' : 'Hide rider'}</button>
          <p className="rider-sheet-hint">{hidden
            ? 'Hidden riders appear only as their generated name and default colour on this device.'
            : 'Show this rider only as their generated name and default colour on this device. Rounds are not affected.'}</p>
          <button type="button" className="btn-quiet is-danger" onClick={() => { setNotice(null); setView('report'); }}>Report rider</button>
          <p className="rider-sheet-hint">Tell us about an offensive name or cheating. Reports are private.</p>
          {status}
        </div> : reported ? <div className="rider-sheet-actions">
          {status}
          {!hidden && <button type="button" className="btn-quiet" onClick={toggleHidden}>Hide rider too</button>}
          <button type="button" className="btn-settings" data-autofocus onClick={onClose}>Done</button>
        </div> : <form className="rider-report" onSubmit={submit}>
          <fieldset disabled={sending}>
            <legend>What's wrong?</legend>
            {REASONS.map((option, index) => (
              <label key={option.value} className="rider-report-option">
                <input type="radio" name="report-reason" value={option.value} checked={reason === option.value}
                  onChange={() => setReason(option.value)} data-autofocus={index === 0 ? true : undefined} />
                {option.label}
              </label>
            ))}
            <label htmlFor="report-note" className="rider-report-note-label">Add a note (optional)</label>
            <textarea id="report-note" value={note} maxLength={REPORT_NOTE_MAX} rows={3} aria-describedby="report-note-count"
              onChange={event => setNote(event.target.value.slice(0, REPORT_NOTE_MAX))} />
            <small id="report-note-count" className="rider-report-count">{note.length}/{REPORT_NOTE_MAX}</small>
          </fieldset>
          {status}
          <div className="settings-row">
            <button type="submit" className="btn-settings" disabled={!reason || sending}>{sending ? 'Sending…' : 'Send report'}</button>
            <button type="button" className="btn-quiet" disabled={sending} onClick={() => { setNotice(null); setView('menu'); }}>Back</button>
          </div>
        </form>}
      </div>
    </div>
  );
}

/** Mount once on the game screen; renders the sheet for whichever row opened it. */
export const RiderSheetHost: React.FC = () => {
  const target = useSyncExternalStore(subscribeSheet, getSheet, getSheet);
  const close = useCallback(() => closeRiderSheet(), []);
  // Leaving the game screen (sign-out) must not leave a sheet queued for next time.
  useEffect(() => close, [close]);
  if (!target) return null;
  return createPortal(<RiderSheet key={target.playerId} target={target} onClose={close} />, document.body);
};
